import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { GET_DELIVERY_ORDERS } from '../graphql/queries'
import {
  ASSIGN_DELIVERY_RUN_DRIVER,
  CREATE_DELIVERY_RUN,
  DELETE_DELIVERY_RUN,
  GET_DELIVERY_DRIVERS,
  GET_DELIVERY_RUNS,
  SET_DELIVERY_RUN_STOPS,
} from '../graphql/delivery-run'
import { DeliveredBy, DeliveryDriver, DeliveryRunDetail, Order } from '../types/order'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { apiTime, formatEuros, formatTime, localDayBoundsUtc, phoneWithAccessCode } from '../lib/format'
import {
  addressOf,
  canPrintDeliverySlip,
  countedStops,
  deliveredAt,
  etaLabel,
  formatEta,
  hasAddress,
  isOwnDelivery,
  isOwnPlatformDelivery,
  MAX_RUN_STOPS,
  mapsUrl,
  openDeliverySlips,
  roundTripMapsUrl,
  runStopCount,
  STORE_DOOR,
  stopNumbers,
  stopsInOrder,
  stopsNotReady,
  whatsAppMessage,
} from '../lib/delivery'
import { copyText } from '../lib/clipboard'
import { NEUTRAL_ZIPCODE_COLOR, ZipcodeColor, zipcodeArea, zipcodeColor } from '../lib/zipcodeColors'
import { useGeocodedOrders, useRestaurantLocation } from '../hooks/useGeocodedOrders'
import { useNow } from '../hooks/useNow'
import DeliveryMap, { DeliveryPin } from '../components/DeliveryMap'
import { PLATFORM_NAMES, orderSourceBadge } from '../lib/orderSource'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  HomeIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon,
  MapIcon,
  MapPinIcon,
  PhoneIcon,
  PrinterIcon,
  TruckIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline'

// Front-of-house awareness screen, not a kitchen screen. KitchenDisplayPage runs at 3s
// because a cook is waiting on the next ticket; nobody watches this board that intently,
// and the delivery handover doc explicitly asked for restraint — every logged-in terminal
// polls the same API.
const DELIVERY_POLL_MS = 30_000

const FILTER_STORAGE_KEY = 'delivery-board-filter'
const VIEW_STORAGE_KEY = 'delivery-board-view'

type DeliveryFilter = DeliveredBy | 'all'
// Lanes = the status flow; map = the same own-delivery orders by address, for spotting
// which ones sit close together (owner ask 2026-09-10).
type BoardView = 'lanes' | 'map'

// The board's lane order — the service flow left to right.
const LANE_STATUSES = ['SCHEDULED', 'CONFIRMED', 'PREPARING', 'READY', 'IN_DELIVERY', 'COMPLETED'] as const
type LaneStatus = (typeof LANE_STATUSES)[number]
// The query filters on exactly LANE_STATUSES, so every row is a lane order. Narrowed once
// where the rows come in; nothing downstream casts `status` again.
type LaneOrder = Order & { status: LaneStatus }
const NO_ORDERS: LaneOrder[] = []

// Lanes where an order can still be bundled onto a driver's run — i.e. not yet claimed.
// The zipcode chip only lights up for areas with ≥2 orders in these lanes: a colored chip
// MEANS "another order is going that way". Once one of a pair is claimed (IN_DELIVERY),
// bundling with it is moot and both chips go quiet again.
const BUNDLEABLE_STATUSES: readonly string[] = ['SCHEDULED', 'CONFIRMED', 'PREPARING', 'READY']

// One hue per lane: `text` for the lane title, `pin` for the map pin and legend dot. Full
// class literals only — `pin` reaches Leaflet as an HTML string and Tailwind's scanner
// must see it here.
const LANE_COLORS: Record<LaneStatus, { text: string; pin: string }> = {
  SCHEDULED: { text: 'text-cyan-600', pin: 'bg-cyan-500' },
  CONFIRMED: { text: 'text-blue-600', pin: 'bg-blue-600' },
  PREPARING: { text: 'text-orange-600', pin: 'bg-orange-500' },
  READY: { text: 'text-green-600', pin: 'bg-green-600' },
  IN_DELIVERY: { text: 'text-indigo-600', pin: 'bg-indigo-600' },
  COMPLETED: { text: 'text-gray-400', pin: 'bg-gray-400' },
}

// One colour per delivery route, keyed on the route ID — not its place in the list — so a
// route keeps its colour all shift while others are deleted or finish. Hues chosen away
// from the lane colours so a route ring never reads as a status. Full literals: `bg`
// reaches Leaflet's pin HTML, where Tailwind's scanner cannot see a computed name.
const ROUTE_COLORS = [
  { bg: 'bg-fuchsia-600', text: 'text-white' },
  { bg: 'bg-amber-500', text: 'text-gray-900' },
  { bg: 'bg-rose-700', text: 'text-white' },
  { bg: 'bg-lime-500', text: 'text-gray-900' },
  { bg: 'bg-slate-800', text: 'text-white' },
  { bg: 'bg-stone-500', text: 'text-white' },
] as const
const routeColor = (runId: number) => ROUTE_COLORS[runId % ROUTE_COLORS.length]

// Kitchen / delivery state of a stop in the routes panel. READY is what the driver's Start
// waits on, so it is the one chip that must jump out.
const STOP_STATUS: Record<string, { label: string; classes: string }> = {
  SCHEDULED: { label: 'Scheduled', classes: 'bg-cyan-100 text-cyan-800' },
  CONFIRMED: { label: 'Waiting', classes: 'bg-blue-100 text-blue-800' },
  PREPARING: { label: 'Being made', classes: 'bg-orange-100 text-orange-800' },
  READY: { label: 'Ready', classes: 'bg-green-600 text-white' },
  IN_DELIVERY: { label: 'Out', classes: 'bg-indigo-100 text-indigo-800' },
  COMPLETED: { label: 'Delivered', classes: 'bg-gray-100 text-gray-500' },
  CANCELLED: { label: 'Cancelled', classes: 'bg-gray-100 text-gray-400 line-through' },
  REFUNDED: { label: 'Refunded', classes: 'bg-gray-100 text-gray-400 line-through' },
}

// A route's readiness in one pill, so the panel answers "can this one leave?" with every
// route still collapsed. The planned states are the API's Start condition read out loud (a
// driver, and every stop READY); an ACTIVE route is no longer this screen's decision.
const routeState = (
  run: DeliveryRunDetail,
  stopCount: number,
  waitingCount: number
): { label: string; classes: string } => {
  if (run.status !== 'PLANNED') return { label: 'On the road', classes: 'text-indigo-800' }
  if (stopCount === 0) return { label: 'Empty', classes: 'text-gray-500' }
  if (!run.driver) return { label: 'Needs a driver', classes: 'text-amber-800' }
  if (waitingCount > 0) return { label: waitingCount + ' in the kitchen', classes: 'text-amber-800' }
  return { label: 'Ready to start', classes: 'text-green-800' }
}

// How long "Delete route" stays armed after the first tap.
const CONFIRM_ARM_MS = 4_000

// How many delivered orders stay visible before the "show more" toggle. The query is
// already windowed to today, so the tail is bounded — this just keeps the lane scannable.
const COMPLETED_PREVIEW_COUNT = 5

// `platform` is NULL for an unrecognised HubRise channel, while delivered_by still
// fails safe to 'platform' — so an external card must always have a badge to show.
// Names come from lib/orderSource.ts so the board and the history browser agree.
const platformLabel = (platform?: string | null): string =>
  (platform && PLATFORM_NAMES[platform]) || 'Platform'

const laneTitle = (status: LaneStatus, filter: DeliveryFilter): string => {
  switch (status) {
    case 'SCHEDULED':
      return 'Scheduled'
    case 'CONFIRMED':
      return 'Waiting'
    case 'PREPARING':
      return 'Being made'
    case 'READY':
      if (filter === 'own') return 'Ready for driver'
      if (filter === 'platform') return 'Ready for courier'
      return 'Ready'
    case 'IN_DELIVERY':
      return 'Out for delivery'
    case 'COMPLETED':
      return 'Delivered'
  }
}

const isOwn = (order: Order): boolean => order.delivered_by === 'own'

// addressOf / hasAddress / mapsUrl live in lib/delivery.ts, shared with DriverPage —
// the TB null-house-number join rule must not exist twice.

// Every time key on this page goes through apiTime: an unreadable timestamp reads as 0
// instead of NaN, which would silently flatten any ordering that falls back to it. See
// lib/format.ts.
const sortKey = (order: Order): number => apiTime(order.scheduled_for || order.created_at)

const completedSortKey = (order: Order): number =>
  apiTime(order.current_status_since || order.scheduled_for || order.created_at)

// ASAP or pre-order is the operator's first question about a pin: an ASAP order is
// already waiting, a pre-order is due at a time and must not go out early. A pre-order
// without a time cannot be read as one, so it falls back to ASAP rather than showing a
// blank clock.
const isPreorder = (order: Order): boolean => Boolean(order.is_preorder && order.scheduled_for)

const orderedAt = (order: Order): number => apiTime(order.created_at)

// The map's own ordering, and the ranking behind the ASAP pin numbers: a pre-order is
// placed by when it is DUE, an ASAP order by when it was ORDERED. Deliberately not the
// lanes' sortKey — that reads scheduled_for first, which on an ASAP order is a
// server-set target time, not the moment the customer ordered.
const mapSortKey = (order: Order): number => (isPreorder(order) ? sortKey(order) : orderedAt(order))

const DeliveryBoardPage: React.FC = () => {
  const [filter, setFilter] = useState<DeliveryFilter>(
    () => (localStorage.getItem(FILTER_STORAGE_KEY) as DeliveryFilter) ?? 'all'
  )
  const [showAllCompleted, setShowAllCompleted] = useState(false)
  const [view, setView] = useState<BoardView>(() =>
    localStorage.getItem(VIEW_STORAGE_KEY) === 'map' ? 'map' : 'lanes'
  )
  // The order whose card sits beside the map. Kept as an id, not the object: the poll
  // replaces order objects every tick and the card must follow the fresh one.
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  // Statuses the operator switched off on the map (owner ask 2026-09-10: "perhaps they
  // don't want to see specific ones"). Deliberately NOT persisted, unlike the view
  // toggle: a status hidden to declutter at peak must not still be hiding tomorrow's
  // pre-orders on a terminal that opens straight into the map.
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<LaneStatus>>(() => new Set())

  const toggleStatus = (status: LaneStatus) => {
    const next = new Set(hiddenStatuses)
    if (next.has(status)) next.delete(status)
    else next.add(status)
    setHiddenStatuses(next)
  }

  const selectFilter = (next: DeliveryFilter) => {
    setFilter(next)
    localStorage.setItem(FILTER_STORAGE_KEY, next)
  }

  const selectView = (next: BoardView) => {
    setView(next)
    localStorage.setItem(VIEW_STORAGE_KEY, next)
  }

  // Recomputed every render, never memoized at mount: an always-on POS terminal crosses
  // midnight while mounted, and a frozen window would keep querying yesterday.
  const { scheduledFrom: todayStartIso, scheduledTo } = localDayBoundsUtc()
  const todayStartMs = new Date(todayStartIso).getTime()

  // Start the window a day early, exactly as KitchenDisplayPage does. An order that goes
  // out for delivery at 23:50 is still out at 00:05, and a today-only window would drop it
  // off the board with no way left to mark it delivered. Stepping the local date back by
  // one (rather than subtracting 24h) keeps this correct across a DST change.
  const lookbackStart = new Date(todayStartMs)
  lookbackStart.setDate(lookbackStart.getDate() - 1)

  const { data, loading, error, refetch } = useQuery(GET_DELIVERY_ORDERS, {
    variables: {
      filters: {
        order_type: 'DELIVERY',
        statuses: [...LANE_STATUSES],
        scheduled_from: lookbackStart.toISOString(),
        scheduled_to: scheduledTo,
      },
      sortField: 'SCHEDULED_FOR',
      sortDirection: 'ASC',
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: DELIVERY_POLL_MS,
    notifyOnNetworkStatusChange: true,
  })

  // Routes. Their own queries — graphql/delivery-run.ts says why they stay out of
  // recentOrders. The routes themselves are readable by every role that can open this board
  // (the API accepts delivery.plan OR any order.list.* since 2026-09-30): without
  // delivery.plan the panel is read-only. Only the driver picker's list stays behind the
  // permission, so a read-only board never sends the one request the API would refuse.
  const { hasPermission } = usePermissions()
  const canPlan = hasPermission(PERMISSIONS.DELIVERY_PLAN)
  const {
    data: runsData,
    error: runsError,
    refetch: refetchRuns,
  } = useQuery(GET_DELIVERY_RUNS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: DELIVERY_POLL_MS,
  })
  const { data: driversData } = useQuery(GET_DELIVERY_DRIVERS, {
    skip: !canPlan,
    fetchPolicy: 'cache-and-network',
  })
  // The terminal's clock, for the overdue reading of an arrival estimate: ticks with the poll
  // so "n min over" keeps counting even when a poll brings nothing new to render.
  const now = useNow(DELIVERY_POLL_MS)
  const [createRun] = useMutation(CREATE_DELIVERY_RUN)
  const [assignRunDriver] = useMutation(ASSIGN_DELIVERY_RUN_DRIVER)
  const [setRunStops] = useMutation(SET_DELIVERY_RUN_STOPS)
  const [deleteRun] = useMutation(DELETE_DELIVERY_RUN)

  // Per-route lock: the ref is the synchronous guard (React batches state updates, so a
  // state-only guard misses a same-render double tap); the state mirror drives the UI.
  // Per route, so editing one route never freezes another.
  const busyRunRef = useRef<Set<number>>(new Set())
  const [busyRunIds, setBusyRunIds] = useState<Set<number>>(new Set())
  const creatingRunRef = useRef(false)
  const [creatingRun, setCreatingRun] = useState(false)
  // Which route is open in the panel — one at a time, and from two routes up none of them
  // until the operator taps a header. undefined = not chosen yet, which is what lets a lone
  // route open itself without overriding a deliberate collapse (see openRunId).
  const [expandedRunId, setExpandedRunId] = useState<number | null | undefined>(undefined)
  // Two-step delete: one stray tap would otherwise drop a planned route's driver and order.
  const [armedDeleteRunId, setArmedDeleteRunId] = useState<number | null>(null)
  const armTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (armTimerRef.current) clearTimeout(armTimerRef.current)
    },
    []
  )

  // Every refetch on this page is best-effort convergence, never a source of truth: the
  // poll re-runs on its own and status changes are shielded locally. Swallow rejections
  // so a network blip can neither surface as a false failure nor escape as an unhandled
  // promise rejection.
  const settle = useCallback(() => {
    refetch().catch(() => undefined)
    refetchRuns().catch(() => undefined)
  }, [refetch, refetchRuns])

  useEffect(() => {
    window.addEventListener('focus', settle)
    return () => window.removeEventListener('focus', settle)
  }, [settle])

  const allOrders: LaneOrder[] = useMemo(
    () => (data?.recentOrders ?? []) as LaneOrder[],
    [data]
  )

  // PLANNED and ACTIVE routes (the query leaves COMPLETED out by default): active first —
  // those are on the road now — then planned, each by id so a poll never reshuffles them.
  const runs: DeliveryRunDetail[] = useMemo(() => {
    const list = ((runsData?.deliveryRuns ?? []) as DeliveryRunDetail[]).filter(
      run => run.status !== 'COMPLETED'
    )
    const rank = (run: DeliveryRunDetail) => (run.status === 'ACTIVE' ? 0 : 1)
    return [...list].sort((a, b) => rank(a) - rank(b) || a.id - b.id)
  }, [runsData])

  // A lone route has nothing to choose between, so it opens itself; from two up the panel is
  // a list of headers until the operator picks one. Any explicit tap — collapsing everything
  // included — wins over that default, until the route it named leaves the list (deleted, or
  // finished and filtered out): the choice then lapses instead of leaving the panel pointing
  // at a route that is no longer there.
  const chosen =
    typeof expandedRunId === 'number' && !runs.some(run => run.id === expandedRunId)
      ? undefined
      : expandedRunId
  const openRunId = chosen === undefined ? (runs.length === 1 ? runs[0].id : null) : chosen

  const drivers = (driversData?.deliveryDrivers ?? []) as DeliveryDriver[]

  // Which route an order is in, and at which stop — built from the routes' own stop lists,
  // the source of truth. Never from an order's delivery_run_* fields: an order just taken
  // out of a route keeps its old fields in the cache until something refetches that order.
  const routeOf = useMemo(() => {
    const map = new Map<number, { run: DeliveryRunDetail; position: number }>()
    // Numbered as a person counts (lib/delivery.ts stopNumbers), matching stops_total. A
    // stop cancelled on an active route has no number and stays out of this map — a
    // cancelled order is on neither the lanes nor the map anyway.
    for (const run of runs) {
      stopNumbers(run.stops).forEach((position, orderId) => {
        map.set(orderId, { run, position })
      })
    }
    return map
  }, [runs])

  // Counts come from the unfiltered set so the segment labels stay honest whichever
  // segment is active.
  const counts = useMemo(
    () => ({
      own: allOrders.filter(isOwn).length,
      platform: allOrders.filter(o => !isOwn(o)).length,
      all: allOrders.length,
    }),
    [allOrders]
  )

  const visibleOrders = useMemo(() => {
    if (filter === 'all') return allOrders
    if (filter === 'own') return allOrders.filter(isOwn)
    return allOrders.filter(o => !isOwn(o))
  }, [allOrders, filter])

  // Bundleable own orders per zipcode area. Grouped over the UNFILTERED set so the group
  // signal doesn't flicker when switching segments (own/external/all show the same truth).
  // Holds the orders themselves, not counts — the bundle-print button needs them.
  //
  // Two narrowings beyond "own + not yet claimed":
  //  - TODAY only. The query deliberately looks back a day so nothing active vanishes at
  //    midnight, but an order stranded in a pre-claim lane since yesterday is not going
  //    out on this run — counting it would light up a bogus group and put a dead slip in
  //    the bundle print.
  //  - Printable only (canPrintDeliverySlip: own + a street). An address-less order can't
  //    be driven anywhere, so it is not bundleable, and this keeps "Print N" honest — the
  //    count and the slips it opens are the same set.
  //  - Not already in a delivery route. The PC4 group is a SUGGESTION for building a run;
  //    once an operator has put an order in a real route, that route's stop order is the
  //    truth and the order must not keep advertising a bundle (or ride along in its
  //    "Print N" / "Route N").
  const bundleableAreaOrders = useMemo(() => {
    const groups = new Map<string, Order[]>()
    for (const o of allOrders) {
      if (!canPrintDeliverySlip(o) || !BUNDLEABLE_STATUSES.includes(o.status)) continue
      if (routeOf.has(o.id)) continue
      if (sortKey(o) < todayStartMs) continue
      const area = zipcodeArea(o.delivery_postcode)
      if (!area) continue
      const group = groups.get(area)
      if (group) group.push(o)
      else groups.set(area, [o])
    }
    return groups
  }, [allOrders, todayStartMs, routeOf])

  // The bundle a card or pin belongs to, or null. Membership, not just "my area has a
  // group": the memo excludes stranded and address-less orders, and such an order must
  // not advertise a bundle it isn't part of. Own orders only — platform orders never
  // carry an address. An IN_DELIVERY order is not in the memo, so it never lights up for
  // a group it can no longer join. One rule for the card header AND the pin ring.
  const bundleOf = (
    order: Order
  ): { area: string; orders: Order[]; color: ZipcodeColor } | null => {
    const area = isOwn(order) ? zipcodeArea(order.delivery_postcode) : null
    if (!area) return null
    const orders = bundleableAreaOrders.get(area) ?? []
    return orders.length >= 2 && orders.includes(order)
      ? { area, orders, color: zipcodeColor(area) }
      : null
  }

  // What the map shows: own orders with an address that are not yet delivered. Delivered
  // addresses are noise when the point is spotting a run to bundle, and the segment
  // filter is deliberately ignored — platform orders have no address to pin anyway.
  // Same day-lookback as the lanes: an order still out at 00:05 stays on the map.
  const mapOrders = useMemo(
    () =>
      allOrders
        .filter(o => isOwn(o) && hasAddress(o) && o.status !== 'COMPLETED')
        .sort((a, b) => mapSortKey(a) - mapSortKey(b)),
    [allOrders]
  )

  // Suggested queue position per ASAP order NOT yet in a route: 1 = ordered longest ago, so
  // it goes out first. An order in a route is numbered by its real stop instead, so it
  // leaves this ranking. Ranked over the UNFILTERED map set, so hiding a lane in the legend
  // cannot renumber the pins.
  const asapRank = useMemo(() => {
    const ranks = new Map<number, number>()
    mapOrders
      .filter(o => !isPreorder(o) && !routeOf.has(o.id))
      .sort((a, b) => orderedAt(a) - orderedAt(b))
      .forEach((order, index) => ranks.set(order.id, index + 1))
    return ranks
  }, [mapOrders, routeOf])

  // Geocoded over the UNFILTERED set so toggling a status on again is instant — the
  // lookups are already done (or cached) rather than starting when the pins are wanted.
  // But only while the map is showing: a terminal that lives on the lanes never calls
  // PDOK at all.
  const coordsOf = useGeocodedOrders(view === 'map' ? mapOrders : NO_ORDERS)
  const home = useRestaurantLocation()

  const visibleMapOrders = useMemo(
    () => mapOrders.filter(o => !hiddenStatuses.has(o.status)),
    [mapOrders, hiddenStatuses]
  )

  const pins = useMemo<DeliveryPin[]>(() => {
    const result: DeliveryPin[] = []
    for (const order of visibleMapOrders) {
      const point = coordsOf(order)
      if (!point) continue
      const { line1 } = addressOf(order)
      const via = `via ${orderSourceBadge(order).label}`
      const precision = point.precision === 'address' ? '' : ' (postcode only)'

      // In a route: its real stop order replaces the suggestion — a square pin in the
      // route's colour, carrying the stop number.
      const route = routeOf.get(order.id)
      if (route) {
        result.push({
          id: order.id,
          position: point,
          precise: point.precision === 'address',
          laneBg: LANE_COLORS[order.status].pin,
          groupBg: routeColor(route.run.id).bg,
          kind: 'stop',
          badge: String(route.position),
          label: `#${order.order_number.slice(-5)} · ${line1} · Route ${route.run.id}, stop ${
            route.position
          } of ${route.run.stops_total} · ${via}${precision}`,
        })
        continue
      }

      const preorder = isPreorder(order)
      const rank = asapRank.get(order.id)
      const type = preorder
        ? `pre-order, arrives ${formatTime(order.scheduled_for as string)}`
        : `ASAP #${rank ?? '?'}${order.created_at ? `, ordered ${formatTime(order.created_at)}` : ''}`
      result.push({
        id: order.id,
        position: point,
        precise: point.precision === 'address',
        laneBg: LANE_COLORS[order.status].pin,
        groupBg: bundleOf(order)?.color.bg ?? null,
        kind: preorder ? 'preorder' : 'asap',
        badge: preorder ? formatTime(order.scheduled_for as string) : String(rank ?? ''),
        label: `#${order.order_number.slice(-5)} · ${line1} · ${type} · ${via}${precision}`,
      })
    }
    return result
    // bundleOf is a closure over bundleableAreaOrders, which is the memo's real input.
  }, [visibleMapOrders, coordsOf, bundleableAreaOrders, asapRank, routeOf])

  const lanes = useMemo(() => {
    const grouped = {} as Record<LaneStatus, LaneOrder[]>
    for (const status of LANE_STATUSES) {
      const items = visibleOrders.filter(o => o.status === status)
      grouped[status] =
        status === 'COMPLETED'
          // The query looks back a day so nothing ACTIVE vanishes at midnight, but
          // "Delivered" still means delivered today — re-narrow here on the completion
          // time. An order finished at 00:10 that was out since yesterday stays; one
          // finished at 23:58 yesterday drops off at midnight, which is what staff expect.
          ? items
              .filter(o => completedSortKey(o) >= todayStartMs)
              .sort((a, b) => completedSortKey(b) - completedSortKey(a))
          : items.sort((a, b) => sortKey(a) - sortKey(b))
    }
    return grouped
  }, [visibleOrders, todayStartMs])

  // --- Route planning actions --------------------------------------------------------------
  // Every change sends the route's WHOLE stop list (setDeliveryRunStops: one call covers
  // add, remove and reorder), computed from the route as the API last returned it. On a
  // rejection the global errorLink has already toasted the API's message and the board is
  // stale — someone else changed the route — so it only converges; nothing toasts twice.
  const withRunLock = async (runId: number, action: () => Promise<unknown>) => {
    if (busyRunRef.current.has(runId)) return
    busyRunRef.current.add(runId)
    setBusyRunIds(prev => new Set(prev).add(runId))
    try {
      await action()
    } catch {
      settle()
    } finally {
      busyRunRef.current.delete(runId)
      setBusyRunIds(prev => {
        const next = new Set(prev)
        next.delete(runId)
        return next
      })
    }
  }

  const stopIdsOf = (run: DeliveryRunDetail): number[] => stopsInOrder(run.stops).map(stop => stop.id)

  const sendStops = (run: DeliveryRunDetail, orderIds: number[]) =>
    withRunLock(run.id, () => setRunStops({ variables: { id: run.id, orderIds } }))

  // Opens the route it landed in: the operator's next glance is "did it go where I meant,
  // and in which position" — which is exactly what the panel hides while collapsed.
  const addStop = (run: DeliveryRunDetail, orderId: number) => {
    setExpandedRunId(run.id)
    return sendStops(run, [...stopIdsOf(run), orderId])
  }

  const removeStop = (run: DeliveryRunDetail, orderId: number) =>
    sendStops(run, stopIdsOf(run).filter(id => id !== orderId))

  const moveStop = (run: DeliveryRunDetail, orderId: number, delta: -1 | 1) => {
    const ids = stopIdsOf(run)
    const from = ids.indexOf(orderId)
    const to = from + delta
    if (from < 0 || to < 0 || to >= ids.length) return
    const swapped = [...ids]
    swapped[from] = ids[to]
    swapped[to] = ids[from]
    void sendStops(run, swapped)
  }

  const assignDriver = (run: DeliveryRunDetail, value: string) =>
    withRunLock(run.id, () =>
      assignRunDriver({ variables: { id: run.id, driverUserId: value ? Number(value) : null } })
    )

  // A new route, optionally seeded with orders. Two calls, create then set its stops: if the
  // second is refused, an empty planned route is left behind — visible in the panel and
  // deletable, which beats silently losing the operator's tap. createDeliveryRun adds a
  // route to the list, so the list is refetched rather than patched.
  const createRoute = async (orderIds: number[]) => {
    if (creatingRunRef.current) return
    creatingRunRef.current = true
    setCreatingRun(true)
    try {
      const { data: created } = await createRun({ variables: { driverUserId: null } })
      const run = created?.createDeliveryRun as DeliveryRunDetail | undefined
      // A route made on purpose is the one being worked on, so it opens — and with two or
      // more routes it would otherwise appear as one more collapsed header.
      if (run) setExpandedRunId(run.id)
      if (run && orderIds.length > 0) await setRunStops({ variables: { id: run.id, orderIds } })
      await refetchRuns()
    } catch {
      settle()
    } finally {
      creatingRunRef.current = false
      setCreatingRun(false)
    }
  }

  const onDeleteRun = (run: DeliveryRunDetail) => {
    if (armTimerRef.current) clearTimeout(armTimerRef.current)
    armTimerRef.current = null
    if (armedDeleteRunId !== run.id) {
      setArmedDeleteRunId(run.id)
      armTimerRef.current = setTimeout(() => {
        armTimerRef.current = null
        setArmedDeleteRunId(null)
      }, CONFIRM_ARM_MS)
      return
    }
    setArmedDeleteRunId(null)
    void withRunLock(run.id, async () => {
      await deleteRun({ variables: { id: run.id } })
      await refetchRuns()
    })
  }

  // The planning buttons on the SELECTED order's card in the map view (never on the six
  // lane columns, which would drown in buttons). Mirrors the API's own eligibility for a
  // stop: first-party delivery, not yet out, not in another route.
  const renderPlanningActions = (order: Order) => {
    const route = routeOf.get(order.id)
    if (route) {
      if (route.run.status !== 'PLANNED') return null
      return (
        <button
          onClick={() => removeStop(route.run, order.id)}
          disabled={busyRunIds.has(route.run.id)}
          className="mt-2 w-full py-2.5 flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-wide text-gray-700 bg-white border-2 border-gray-200 active:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <XMarkIcon className="w-4 h-4" />
          Remove from route {route.run.id}
        </button>
      )
    }
    if (!isOwnDelivery(order) || !BUNDLEABLE_STATUSES.includes(order.status)) return null
    const withRoom = runs.filter(run => run.status === 'PLANNED' && run.stops.length < MAX_RUN_STOPS)
    return (
      <div className="mt-2 space-y-2">
        {withRoom.map(run => {
          const color = routeColor(run.id)
          return (
            <button
              key={run.id}
              onClick={() => addStop(run, order.id)}
              disabled={busyRunIds.has(run.id)}
              className={`w-full py-2.5 flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-wide rounded-lg transition-opacity disabled:opacity-50 ${color.bg} ${color.text}`}
            >
              <PlusIcon className="w-4 h-4" />
              Add to route {run.id} ({run.stops.length}/{MAX_RUN_STOPS})
            </button>
          )
        })}
        <button
          onClick={() => createRoute([order.id])}
          disabled={creatingRun}
          className="w-full py-2.5 flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-wide text-gray-700 bg-white border-2 border-dashed border-gray-300 active:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          {creatingRun ? 'Creating…' : 'New route with this order'}
        </button>
      </div>
    )
  }

  // One route in the panel. Collapsed to its header until the operator opens it (one at a
  // time — see openRunId): three expanded routes are taller than the whole column, and the
  // selected order's card below them is where "Add to route N" lives. PLANNED: driver
  // picker and stops with ▲ ▼ ✕ — for delivery.plan only; any other role that can open the
  // board sees the same route read-only. ACTIVE: read-only for everyone — nobody but the
  // driver changes a started route — with the estimate per stop still out, the real time per
  // stop delivered, and the store as the closing row with the driver's expected return.
  const renderRoute = (run: DeliveryRunDetail) => {
    const color = routeColor(run.id)
    const planned = run.status === 'PLANNED'
    const busy = busyRunIds.has(run.id)
    const stops = stopsInOrder(run.stops)
    const numbers = stopNumbers(run.stops)
    const waiting = stopsNotReady(run)
    // Every stop the driver rides to, delivered ones included: the link leaves from the
    // restaurant, so a route already out shows its whole trip — not the remaining stops
    // counted from a door the driver left long ago.
    const mapsStops = countedStops(run.stops)
    const armed = armedDeleteRunId === run.id
    const open = openRunId === run.id
    const state = routeState(run, stops.length, waiting.length)
    // When the driver is expected back at the store (null while PLANNED, or without an
    // estimate): plain in the header, with the overdue reading in the store row.
    const back = planned ? null : etaLabel(run.expected_back_at, now)
    // The assigned driver may have lost delivery.drive since, and then is not in the picker
    // list — keep them selectable so the select shows the truth instead of "No driver yet".
    const driverOptions =
      run.driver && !drivers.some(d => d.id === run.driver?.id) ? [run.driver, ...drivers] : drivers

    return (
      <div key={run.id} className={busy ? 'opacity-60' : ''}>
        {/* The whole header is the toggle, not a chevron a finger has to hunt for. Collapsed,
            it still carries what the operator triages on: how full the route is, who drives
            it, and whether it could leave now — the state pill sits on white so it reads on
            every route colour. Phrasing content only: a <button> may not wrap a <div>. */}
        <button
          onClick={() => setExpandedRunId(open ? null : run.id)}
          aria-expanded={open}
          className={`w-full px-3 py-2.5 min-h-[60px] flex items-center gap-2 text-left ${color.bg} ${color.text}`}
        >
          <span className="flex-1 min-w-0 flex flex-col gap-1">
            <span className="flex items-center gap-2">
              <span className="text-sm font-black">Route {run.id}</span>
              <span className="text-[10px] font-black uppercase tracking-wide opacity-90">
                {planned
                  ? `${stops.length}/${MAX_RUN_STOPS} stops`
                  : `${run.stops_delivered}/${run.stops_total} delivered`}
              </span>
            </span>
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                className={`flex-shrink-0 px-1.5 py-0.5 rounded-full bg-white/95 text-[10px] font-black uppercase tracking-wide ${state.classes}`}
              >
                {state.label}
              </span>
              <span className="truncate text-[11px] font-bold opacity-90">
                {planned
                  ? (run.driver?.name ?? '')
                  : [
                      run.driver?.name ?? 'No driver',
                      run.started_at ? `departed ${formatTime(run.started_at)}` : null,
                      back ? `back ${formatEta(run.expected_back_at)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
              </span>
            </span>
          </span>
          {open ? (
            <ChevronUpIcon className="w-5 h-5 flex-shrink-0" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 flex-shrink-0" />
          )}
        </button>

        {open && (
          <div className="p-3 space-y-2">
            {/* PLANNED planners only: this is an action, not a repeat of the header — which
                already names the driver of a route that has left or of a read-only view. */}
            {planned && canPlan && (
              <label className="flex items-center gap-2 text-xs font-bold text-gray-600">
                Driver
                <select
                  value={run.driver?.id ?? ''}
                  disabled={busy}
                  onChange={e => assignDriver(run, e.target.value)}
                  className="flex-1 min-w-0 h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm font-semibold text-gray-900"
                >
                  <option value="">No driver yet</option>
                  {driverOptions.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {stops.length === 0 ? (
              <div className="text-xs font-semibold text-gray-400">
                {canPlan ? `No stops yet. Tap a pin on the map, then “Add to route ${run.id}”.` : 'No stops yet.'}
              </div>
            ) : (
              <ol className="space-y-1">
                {stops.map((stop, index) => {
                  const status = STOP_STATUS[stop.status] ?? {
                    label: stop.status,
                    classes: 'bg-gray-100 text-gray-500',
                  }
                  // A stop still out shows its estimate, amber once overdue; a delivered one
                  // its real time. Nothing while PLANNED: no estimate exists before Start.
                  const eta = !planned && stop.status === 'IN_DELIVERY' ? etaLabel(stop.delivery_eta_at, now) : null
                  const delivered = planned ? null : deliveredAt(stop)
                  return (
                    <li
                      key={stop.id}
                      className={`flex items-center gap-1.5 ${eta?.overdue ? 'rounded-md bg-amber-100 -mx-1.5 px-1.5' : ''}`}
                    >
                      <button
                        onClick={() => setSelectedOrderId(stop.id)}
                        className={`flex-1 min-w-0 flex items-center gap-2 py-1 text-left rounded-md ${
                          selectedOrderId === stop.id ? 'bg-gray-100' : ''
                        }`}
                      >
                        <span
                          className={`w-6 h-6 flex-shrink-0 rounded-md flex items-center justify-center text-[11px] font-black ${color.bg} ${color.text}`}
                        >
                          {numbers.get(stop.id) ?? '–'}
                        </span>
                        <span className="text-xs font-black text-gray-900">#{stop.order_number.slice(-5)}</span>
                        <span className="flex-1 min-w-0 truncate text-xs text-gray-700">
                          {addressOf(stop).line1}
                        </span>
                        {eta && (
                          <span
                            className={`text-[11px] whitespace-nowrap ${
                              eta.overdue ? 'font-black text-amber-800' : 'font-bold text-indigo-700'
                            }`}
                          >
                            {eta.text}
                          </span>
                        )}
                        {delivered && (
                          <span className="text-[11px] font-bold text-gray-600 whitespace-nowrap">{delivered}</span>
                        )}
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[10px] font-black whitespace-nowrap ${status.classes}`}
                        >
                          {status.label}
                        </span>
                      </button>
                      {planned && canPlan && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => moveStop(run, stop.id, -1)}
                            disabled={busy || index === 0}
                            aria-label="Move stop up"
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 active:bg-gray-100 disabled:opacity-30"
                          >
                            <ArrowUpIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveStop(run, stop.id, 1)}
                            disabled={busy || index === stops.length - 1}
                            aria-label="Move stop down"
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 active:bg-gray-100 disabled:opacity-30"
                          >
                            <ArrowDownIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeStop(run, stop.id)}
                            disabled={busy}
                            aria-label="Remove stop from route"
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 active:bg-gray-100 disabled:opacity-30"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </li>
                  )
                })}
                {/* The store is the last location of every route (owner 2026-09-30): the
                    closing row carries the driver's expected return, read like a stop's
                    estimate — amber once overdue — and nothing when there is none. Only on a
                    route that has left: before Start there is no estimate to show. */}
                {!planned && (
                  <li
                    className={`flex items-center gap-2 py-1 mt-1 border-t border-dashed border-gray-300 ${
                      back?.overdue ? 'rounded-b-md bg-amber-100 -mx-1.5 px-1.5' : ''
                    }`}
                  >
                    <span className="w-6 h-6 flex-shrink-0 rounded-md border-2 border-gray-400 bg-white text-gray-600 flex items-center justify-center">
                      <HomeIcon className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-xs font-black text-gray-900 whitespace-nowrap">Back at the store</span>
                    <span className="flex-1 min-w-0 truncate text-xs text-gray-700">{STORE_DOOR}</span>
                    {back && (
                      <span
                        className={`text-xs font-black whitespace-nowrap ${
                          back.overdue ? 'text-amber-800' : 'text-indigo-700'
                        }`}
                      >
                        {back.text}
                      </span>
                    )}
                  </li>
                )}
              </ol>
            )}

            <div className="flex items-center gap-2 pt-1">
              {mapsStops.length > 0 && (
                <a
                  href={roundTripMapsUrl(mapsStops)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 h-10 inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-100 active:bg-gray-200 text-gray-800 text-xs font-black uppercase tracking-wide"
                >
                  <MapPinIcon className="w-4 h-4" />
                  Route in Maps
                </a>
              )}
              {planned && canPlan && (
                <button
                  onClick={() => onDeleteRun(run)}
                  disabled={busy}
                  className={`h-10 px-3 inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-black uppercase tracking-wide transition-colors disabled:opacity-50 ${
                    armed ? 'bg-red-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-600'
                  } ${mapsStops.length > 0 ? '' : 'flex-1'}`}
                >
                  <TrashIcon className="w-4 h-4" />
                  {armed ? 'Tap again to delete' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Shown to everyone who can open the board (owner 2026-09-30: anybody who looks at the
  // delivery overview sees every location's expected time). Without delivery.plan it is a
  // read-only view of the same routes: no New route, no driver picker, no ▲ ▼ ✕, no Delete.
  const renderRoutesPanel = () => {
    return (
      <section className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-wide text-gray-700">
              Routes{runs.length > 0 ? ' · ' + runs.length : ''}
            </div>
            <div className="text-[11px] font-semibold text-gray-500">
              {canPlan ? 'Tap a pin, then add it to a route' : 'Read-only on this account'}
            </div>
          </div>
          {canPlan && (
            <button
              onClick={() => createRoute([])}
              disabled={creatingRun}
              className="h-10 px-3 inline-flex items-center gap-1 rounded-lg bg-gray-900 active:bg-black text-white text-xs font-black uppercase tracking-wide disabled:opacity-50"
            >
              <PlusIcon className="w-4 h-4" />
              {creatingRun ? 'Creating…' : 'New route'}
            </button>
          )}
        </div>
        {runsError && !runsData && (
          <div className="px-3 py-2 text-xs font-semibold text-red-800 bg-red-50">
            Routes are not available right now. {runsError.message}
          </div>
        )}
        {runs.length === 0 && !runsError && (
          <div className="px-3 py-4 text-center text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            No routes planned
          </div>
        )}
        {/* Bounded from lg up, where this column has a fixed height it shares with the
            selected order's card and the suggestion list: collapsed headers fit inside it
            without a scrollbar, and an opened route scrolls here instead of pushing the card
            out of reach. overscroll-contain keeps a flick in this list rather than dragging
            the column behind it. Below lg the page scrolls as one, so no inner scroller. */}
        <div className="divide-y divide-gray-200 lg:max-h-[45vh] lg:overflow-y-auto lg:overscroll-contain">
          {runs.map(renderRoute)}
        </div>
      </section>
    )
  }

  // Rendered via plain function calls, not <Card />/<Lane /> components declared here:
  // a component type declared inside the parent gets a new identity every render, so
  // React would unmount and remount every lane on each poll tick — killing taps
  // mid-gesture and resetting lane scroll. `planning` = the map view's selected card,
  // the one place that offers the route buttons.
  const renderCard = (order: Order, planning = false) => {
    const own = isOwn(order)
    const { line1, line2 } = addressOf(order)
    const area = own ? zipcodeArea(order.delivery_postcode) : null
    const bundle = bundleOf(order)
    const route = routeOf.get(order.id)

    return (
      <div
        key={order.id}
        className={`bg-white rounded-xl shadow-sm overflow-hidden border-l-4 ${
          own ? 'border-green-500' : 'border-indigo-500'
        } ${order.status === 'COMPLETED' ? 'opacity-75' : ''}`}
      >
        {/* Bundle header: cards wearing the SAME color bar can ride together on one
            run. The bar names the area and the group size so the signal reads without
            comparing colors across lanes. */}
        {bundle && (
          <div
            className={`px-3 py-1.5 flex items-center justify-between gap-2 ${bundle.color.bg} ${bundle.color.text}`}
            title="Another order is going to this area — consider bundling"
          >
            <span className="text-xs font-black tracking-wide">{bundle.area}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide">
                {bundle.orders.length} orders this area
              </span>
              <button
                onClick={() => openDeliverySlips(bundle.orders)}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/25 hover:bg-white/40 text-[10px] font-black uppercase tracking-wide transition-colors"
                title="Print a delivery slip for every order in this area"
              >
                <PrinterIcon className="w-3.5 h-3.5" />
                Print {bundle.orders.length}
              </button>
              {/* One Google Maps round trip for the bundle — out from the restaurant, every
                  stop, back again — so the operator sees what riding these together takes. */}
              <a
                href={roundTripMapsUrl(bundle.orders)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/25 hover:bg-white/40 text-[10px] font-black uppercase tracking-wide transition-colors"
                title={
                  runStopCount(bundle.orders) < bundle.orders.length
                    ? `Google Maps takes ${runStopCount(bundle.orders)} stops per trip — the first ${runStopCount(bundle.orders)} of this area`
                    : 'Open one route with every stop in this area'
                }
              >
                <MapPinIcon className="w-3.5 h-3.5" />
                Route {runStopCount(bundle.orders)}
              </a>
            </div>
          </div>
        )}
        <div className="p-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
            <Link
              to={`/orders/${order.id}`}
              className="text-lg font-black text-gray-900 leading-none hover:text-primary-700"
            >
              #{order.order_number.slice(-5)}
            </Link>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                own ? 'bg-green-600 text-white' : 'bg-indigo-600 text-white'
              }`}
            >
              {own ? 'Own' : platformLabel(order.platform)}
            </span>
            {/* Where it was placed, on own deliveries only — an external card's badge
                above already names its platform. "Own" answers who drives; this answers
                what the customer is looking at (our tracking page, or the platform app). */}
            {own && (() => {
              const source = orderSourceBadge(order)
              return (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${source.classes}`}
                  title={source.customerView}
                >
                  via {source.label}
                </span>
              )
            })()}
            {/* Ungrouped cards keep a quiet area chip; grouped ones already name the
                area loudly in the header bar. */}
            {area && !bundle && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-wide whitespace-nowrap ${NEUTRAL_ZIPCODE_COLOR.bg} ${NEUTRAL_ZIPCODE_COLOR.text}`}
              >
                {area}
              </span>
            )}
            {order.is_preorder && order.scheduled_for && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-600 text-white text-[10px] font-black uppercase tracking-wide whitespace-nowrap">
                Pre-order {formatTime(order.scheduled_for)}
              </span>
            )}
            {/* Already planned: the lanes say which route and stop, in the route's colour —
                square like its map pin — so nobody plans the same order twice. */}
            {route && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide whitespace-nowrap ${
                  routeColor(route.run.id).bg
                } ${routeColor(route.run.id).text}`}
              >
                Route {route.run.id} · stop {route.position}
              </span>
            )}
          </div>

          {!order.is_preorder && order.scheduled_for && (
            <div className="text-xs font-bold text-gray-500 mb-1">{formatTime(order.scheduled_for)}</div>
          )}

          {order.customer_name && (
            <div className="text-sm font-semibold text-gray-800 truncate">{order.customer_name}</div>
          )}

          {own ? (
            <>
              {/* Who took it — stamped by claimDelivery, so FOH knows who to call.
                  Survives into the Delivered lane. */}
              {order.delivered_by_user && (
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-indigo-700">
                  <TruckIcon className="w-3.5 h-3.5" />
                  with {order.delivered_by_user.name}
                </div>
              )}

              {order.customer_phone && (
                <div className="mt-1 flex items-center gap-1">
                  <a
                    href={`tel:${order.customer_phone}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                  >
                    <PhoneIcon className="w-3.5 h-3.5" />
                    {order.customer_phone}
                  </a>
                  {/* Uber Eats relay number: the caller keys this code in after the prompt.
                      Shown beside the number, never inside the tel: link (DTMF is unreliable). */}
                  {order.customer_phone_access_code && (
                    <span className="text-xs font-semibold text-gray-700">
                      · code {order.customer_phone_access_code}
                    </span>
                  )}
                  {/* Copy, for typing into WhatsApp — padded to a finger-sized target. */}
                  <button
                    type="button"
                    onClick={() => copyText(phoneWithAccessCode(order), 'Phone number copied')}
                    className="p-2 -my-1.5 rounded-lg text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    aria-label="Copy phone number"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                  </button>
                </div>
              )}

              {hasAddress(order) && (
                <div className="mt-2 text-xs text-gray-700 bg-gray-50 rounded-lg p-2">
                  {line1 && <div className="font-bold">{line1}</div>}
                  {line2 && <div>{line2}</div>}
                  <a
                    href={mapsUrl(order)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 font-bold text-primary-700 hover:underline"
                  >
                    <MapPinIcon className="w-3.5 h-3.5" />
                    Route
                  </a>
                </div>
              )}

              {order.delivery_instructions && (
                <div className="mt-2 text-xs font-bold text-blue-800 bg-blue-50 px-2 py-1 rounded-md">
                  {order.delivery_instructions}
                </div>
              )}
            </>
          ) : (
            <>
              {order.status === 'READY' && (
                <div className="mt-2 text-xs font-bold text-indigo-800 bg-indigo-50 px-2 py-1 rounded-md">
                  Courier collects
                </div>
              )}
            </>
          )}

          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span className="font-bold text-gray-700">{formatEuros(order.total_cents)}</span>
            {Boolean(order.delivery_fee_cents) && (
              <span>incl. {formatEuros(order.delivery_fee_cents as number)} fee</span>
            )}
          </div>

          {/* Own, addressed cards only — canPrintDeliverySlip mirrors the API's own 400
              condition, so the button never opens a tab of raw JSON error. Pointless once
              delivered. Not gated on group membership: a lone order gets a slip too. */}
          {canPrintDeliverySlip(order) && order.status !== 'COMPLETED' && (
            <button
              onClick={() => openDeliverySlips([order])}
              className="mt-2 w-full py-2 flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-wide text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <PrinterIcon className="w-4 h-4" />
              Bezorgbon
            </button>
          )}

          {/* Platform orders we drive only: the platform never hands that customer our
              tracking link, so the operator copies this message and pastes it into WhatsApp by
              hand. Hidden once delivered — "we are working on your order" would be wrong by
              then. The link sentence needs tracking_url, which is NULL on HubRise orders
              ingested before the API issued tokens at ingest; the message then copies
              without it. */}
          {isOwnPlatformDelivery(order) && order.status !== 'COMPLETED' && (
            <button
              onClick={() =>
                copyText(whatsAppMessage(order, order.tracking_url), 'Message copied. Paste it in WhatsApp.')
              }
              className="mt-2 w-full py-2.5 flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-wide text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-lg transition-colors"
            >
              <ChatBubbleLeftRightIcon className="w-4 h-4" />
              Copy WhatsApp message
            </button>
          )}

          {planning && canPlan && renderPlanningActions(order)}

          {/* Deliberately NO advance buttons on this board (owner decision 2026-08-26):
              only the claiming driver moves an own delivery — claim and "delivered" both
              happen on /driver. The fallback for a driver who forgot to tap "delivered"
              is the order detail page (IN_DELIVERY → COMPLETED there). External orders'
              status arrives via HubRise sync. */}
        </div>
      </div>
    )
  }

  const renderLane = (status: LaneStatus) => {
    const items = lanes[status]
    const displayed =
      status === 'COMPLETED' && !showAllCompleted ? items.slice(0, COMPLETED_PREVIEW_COUNT) : items

    return (
      <div key={status} className="flex flex-col h-full min-w-0">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className={`text-sm font-black uppercase tracking-wide ${LANE_COLORS[status].text}`}>
            {laneTitle(status, filter)} ({items.length})
          </h2>
          {status === 'COMPLETED' && items.length > COMPLETED_PREVIEW_COUNT && (
            <button
              onClick={() => setShowAllCompleted(!showAllCompleted)}
              className="text-[11px] font-bold text-gray-500 hover:text-gray-900 flex items-center gap-0.5 flex-shrink-0"
            >
              {showAllCompleted ? (
                <><ChevronUpIcon className="w-3.5 h-3.5" /> Less</>
              ) : (
                <><ChevronDownIcon className="w-3.5 h-3.5" /> {items.length - COMPLETED_PREVIEW_COUNT} more</>
              )}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Wrapped, not map(renderCard): map would pass the index as `planning`. */}
          {displayed.map(order => renderCard(order))}
          {items.length === 0 && (
            <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Empty</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderSegment = (value: DeliveryFilter, label: string, count: number) => (
    <button
      onClick={() => selectFilter(value)}
      className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
        filter === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
      }`}
    >
      {label} ({count})
    </button>
  )

  const renderViewButton = (value: BoardView, label: string, Icon: typeof MapIcon) => (
    <button
      onClick={() => selectView(value)}
      className={`px-3 py-2 text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5 ${
        view === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
      }`}
      title={value === 'map' ? 'Own deliveries on a map' : 'Orders by status'}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )

  // Map view: the map takes the width, a side panel holds the selected order's card (the
  // SAME card as the lanes, so Route / Bezorgbon / bundle print behave identically) and a
  // compact list of everything pinned. Pins still resolving (cold cache) are counted so
  // the operator knows the map is not yet complete.
  const renderMapView = () => {
    const selected =
      selectedOrderId !== null ? visibleMapOrders.find(o => o.id === selectedOrderId) : undefined
    // Three answers per address (see useGeocodedOrders): a point, `undefined` = still
    // being looked up, `null` = cannot be located. Only the second is "pending" — the
    // third must not leave "Locating…" pulsing for the whole shift.
    const locating = visibleMapOrders.filter(o => coordsOf(o) === undefined).length
    const unlocatable = visibleMapOrders.filter(o => coordsOf(o) === null).length
    const countByStatus = (status: LaneStatus) => mapOrders.filter(o => o.status === status).length
    // The suggestion list holds only orders NOT yet in a route; a routed order's order is
    // its route's, shown in the routes panel above.
    const suggestionOrders = visibleMapOrders.filter(o => !routeOf.has(o.id))

    return (
      <div className="flex flex-col lg:flex-row gap-4 lg:flex-1 lg:min-h-0">
        <div className="flex-1 flex flex-col min-h-[420px] lg:min-h-0 min-w-0">
          <DeliveryMap
            pins={pins}
            home={home}
            selectedId={selectedOrderId}
            onSelect={setSelectedOrderId}
            className="flex-1 rounded-xl overflow-hidden border border-gray-200 bg-gray-100"
          />
          {/* The legend doubles as the status filter: tap a status to hide its pins, tap
              again to bring them back. Counts come from the unfiltered set so a hidden
              status still says how many it is hiding. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold text-gray-500">
            {LANE_STATUSES.filter(s => s !== 'COMPLETED').map(status => {
              const hidden = hiddenStatuses.has(status)
              return (
                <button
                  key={status}
                  onClick={() => toggleStatus(status)}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg transition-colors ${
                    hidden ? 'text-gray-400 line-through hover:bg-gray-100' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  title={hidden ? 'Show these orders on the map' : 'Hide these orders from the map'}
                  aria-pressed={!hidden}
                >
                  <span
                    className={`w-3 h-3 rounded-full border border-white shadow-sm ${
                      hidden ? 'bg-gray-300' : LANE_COLORS[status].pin
                    }`}
                  />
                  {laneTitle(status, 'own')} ({countByStatus(status)})
                </button>
              )
            })}
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full border border-dashed border-gray-500" />
              Postcode only
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="w-2.5 h-2.5 rounded-full bg-white" />
              </span>
              Same area
            </span>
            {/* The pin shape IS the order type; these two say what the number on it means. */}
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-gray-500 flex items-center justify-center text-white text-[9px] font-black">
                1
              </span>
              ASAP, longest wait first
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-4 px-1 rounded-full bg-gray-500 flex items-center justify-center text-white text-[9px] font-black">
                18:45
              </span>
              Pre-order, arrival time
            </span>
            {routeOf.size > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-4 h-4 rounded-md bg-gray-500 flex items-center justify-center text-white text-[9px] font-black">
                  2
                </span>
                In a route, stop number
              </span>
            )}
            {locating > 0 && (
              <span className="text-gray-400 animate-pulse">
                Locating {locating} address{locating === 1 ? '' : 'es'}…
              </span>
            )}
            {unlocatable > 0 && (
              <span className="text-gray-400" title="No usable postcode, or the address is not in the national register">
                {unlocatable} not on the map
              </span>
            )}
          </div>
        </div>

        {/* w-96, not w-80: the order list carries six fixed columns since "Via" joined,
            and at w-80 the address column was left with almost nothing. */}
        <aside className="lg:w-96 flex-shrink-0 flex flex-col gap-3 lg:overflow-y-auto lg:pr-1">
          {renderRoutesPanel()}

          {selected ? (
            renderCard(selected, true)
          ) : (
            <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 text-center px-4">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                {mapOrders.length === 0
                  ? 'No own deliveries to show'
                  : visibleMapOrders.length === 0
                    ? 'All statuses hidden — tap one below the map'
                    : 'Tap a pin for the order'}
              </span>
            </div>
          )}

          {suggestionOrders.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* A header only tells the truth if the columns line up, so every column
                  below has a fixed width and the area column is reserved even when a row
                  has no bundle chip. Widths here and in the rows must stay in step. */}
              <div className="px-3 pt-2.5 pb-2 bg-gray-50 border-b border-gray-200">
                <div className="text-[11px] font-black uppercase tracking-wide text-gray-700">
                  {routeOf.size > 0 ? 'Not in a route yet · preferred order' : 'Preferred delivery order'}
                </div>
                <div className="mt-1.5 flex items-center gap-2 text-[10px] font-bold text-gray-400">
                  <span
                    className="w-11 flex-shrink-0"
                    title="Position in the run — ASAP orders are numbered by the time they were ordered. A pre-order shows its arrival time instead."
                  >
                    #
                  </span>
                  <span className="w-14 flex-shrink-0">Order</span>
                  <span
                    className="w-11 flex-shrink-0"
                    title="Where the order was placed. Only webshop customers have our tracking link — Thuisbezorgd and Uber Eats customers follow the order in that platform's app."
                  >
                    Via
                  </span>
                  <span className="flex-1 min-w-0">Address</span>
                  <span className="w-9 flex-shrink-0 text-right" title="Postcode area — same colour means one run">
                    Area
                  </span>
                  <span
                    className="w-11 flex-shrink-0 text-right"
                    title="Order time — when the customer placed the order"
                  >
                    Placed
                  </span>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {suggestionOrders.map(order => {
                  const { line1 } = addressOf(order)
                  const bundle = bundleOf(order)
                  const isSelected = order.id === selectedOrderId
                  const preorder = isPreorder(order)
                  return (
                    <button
                      key={order.id}
                      onClick={() => setSelectedOrderId(order.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${
                        isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Same badge as the pin — round rank for ASAP, time pill for a
                          pre-order — so a row and its pin are recognisably one order. */}
                      <span className="w-11 flex-shrink-0 flex items-center">
                        <span
                          className={`h-5 rounded-full border border-white shadow-sm flex items-center justify-center text-white text-[10px] font-black ${
                            preorder ? 'px-1.5' : 'w-5'
                          } ${LANE_COLORS[order.status].pin}`}
                          title={
                            preorder
                              ? `Pre-order — arrives ${formatTime(order.scheduled_for as string)}`
                              : `ASAP — number ${asapRank.get(order.id) ?? '?'} in the preferred delivery order`
                          }
                        >
                          {preorder
                            ? formatTime(order.scheduled_for as string)
                            : asapRank.get(order.id) ?? ''}
                        </span>
                      </span>
                      <span className="w-14 flex-shrink-0 font-black text-gray-900">
                        #{order.order_number.slice(-5)}
                      </span>
                      {(() => {
                        const source = orderSourceBadge(order)
                        return (
                          <span className="w-11 flex-shrink-0 flex items-center">
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${source.classes}`}
                              title={`${source.label} — ${source.customerView}`}
                            >
                              {source.short}
                            </span>
                          </span>
                        )
                      })()}
                      <span className="flex-1 min-w-0 truncate text-gray-700">{line1}</span>
                      <span className="w-9 flex-shrink-0 flex justify-end">
                        {bundle && (
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${bundle.color.bg} ${bundle.color.text}`}
                          >
                            {bundle.area}
                          </span>
                        )}
                      </span>
                      {/* Always the order time, for pre-orders too — the header names one
                          thing, so the column must hold that one thing. A pre-order's
                          arrival time is the badge in the first column. */}
                      <span className="w-11 flex-shrink-0 text-right text-gray-400 font-bold">
                        {order.created_at ? formatTime(order.created_at) : '—'}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </aside>
      </div>
    )
  }

  // Six lanes side by side need real width. From lg up the board is a fixed-height
  // single row with per-lane scrolling (the POS terminal case); below that the lanes
  // wrap and the page itself scrolls, rather than crushing six lanes into a viewport.
  return (
    <div className="flex flex-col lg:h-[calc(100vh-120px)]">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <TruckIcon className="w-8 h-8 text-gray-900" />
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">DELIVERY</h1>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm font-bold text-gray-400 animate-pulse">
              <div className="w-2 h-2 bg-gray-400 rounded-full" />
              UPDATING…
            </div>
          )}
          {/* Interface preferences, stored per terminal — not server settings. Different
              terminals may reasonably want different views. */}
          <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {renderViewButton('lanes', 'Lanes', ViewColumnsIcon)}
            {renderViewButton('map', 'Map', MapIcon)}
          </div>
          {view === 'lanes' && (
            <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {renderSegment('own', 'Own delivery', counts.own)}
              {renderSegment('platform', 'External', counts.platform)}
              {renderSegment('all', 'All', counts.all)}
            </div>
          )}
        </div>
      </div>

      {error && !data && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm font-semibold text-red-800">
          Could not load delivery orders. {error.message}
        </div>
      )}

      {view === 'map' ? (
        renderMapView()
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:flex-1 lg:min-h-0">
          {LANE_STATUSES.map(renderLane)}
        </div>
      )}
    </div>
  )
}

export default DeliveryBoardPage
